<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Pay Credit Balance
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="submitForm">
            <v-row>
              <v-col sm="12">
                <v-autocomplete
                  v-model="form.user_id"
                  :items="transporters"
                  item-text="display_name"
                  item-value="id"
                  outlined
                  label="Select Transporter *"
                  prepend-inner-icon="mdi-account-tie"
                  :error-messages="errors.user_id"
                  :loading="isLoadingTransporters"
                  clearable
                  @focus="getTransporters"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="4">
                <v-text-field
                  v-model="form.amount_received"
                  outlined
                  label="Amount Received *"
                  prepend-inner-icon="mdi-currency-inr"
                  :error-messages="errors.amount_received"
                  type="number"
                  step="0.01"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col sm="4">
                <v-autocomplete
                  v-model="form.mode_of_payment"
                  :items="paymentModes"
                  outlined
                  label="Mode of Payment *"
                  prepend-inner-icon="mdi-credit-card-outline"
                  :error-messages="errors.mode_of_payment"
                ></v-autocomplete>
              </v-col>
              <v-col sm="4">
                <v-menu
                  v-model="date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.date_of_payment"
                      outlined
                      label="Date of Payment *"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors.date_of_payment"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.date_of_payment"
                    @input="date_menu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="12" class="text-right">
                <v-btn
                  color="grey"
                  outlined
                  class="mr-2"
                  @click="resetForm"
                >
                  Reset
                </v-btn>
                <v-btn
                  color="primary"
                  type="submit"
                  :loading="isLoading"
                  :disabled="isLoading"
                >
                  Submit Payment
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      isLoading: false,
      isLoadingTransporters: false,
      date_menu: false,
      form: {
        user_id: null,
        amount_received: "",
        mode_of_payment: null,
        date_of_payment: new Date().toISOString().substr(0, 10),
      },
      errors: {},
      transporters: [],
      paymentModes: [
        { text: "Cash", value: 1 },
        { text: "Online", value: 2 },
        { text: "Wallet", value: 3 },
        { text: "Credit", value: 4 },
      ],
    };
  },
  mounted() {
    this.getTransporters();
  },
  methods: {
    async getTransporters() {
      if (this.transporters.length > 0) return; // Only fetch if not already loaded
      
      this.isLoadingTransporters = true;
      try {
        // Fetch users with transporter/driver position
        const response = await axios.get("users?is_transporter=true");
        
        if (response.data.success) {
          this.transporters = response.data.data.map(user => {
            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
            const phone = user.phone ? ` - ${user.phone}` : '';
            return {
              id: user.id,
              name: name,
              display_name: `${name}${phone}`,
              email: user.email,
              phone: user.phone,
            };
          });
        } else {
          this.$toast.error("Failed to load transporters");
        }
      } catch (error) {
        console.error("Error fetching transporters:", error);
        this.$toast.error("Failed to load transporters");
      } finally {
        this.isLoadingTransporters = false;
      }
    },
    
    async submitForm() {
      this.isLoading = true;
      this.errors = {};
      
      try {
        const payload = {
          user_id: this.form.user_id,
          amount_received: this.form.amount_received,
          mode_of_payment: this.form.mode_of_payment,
          date_of_payment: this.form.date_of_payment,
        };
        
        const response = await axios.post("payment_receipts", payload);

        if (response.data.success) {
          this.$toast.success(response.data.message || "Payment receipt created successfully!");
          this.resetForm();
          this.$router.push("/pay-credit-balance");
        } else {
          this.$toast.error(response.data.message || "Failed to create payment receipt");
        }
      } catch (error) {
        console.error("Error creating payment receipt:", error);
        
        if (error.response && error.response.status === 422) {
          // Validation errors
          this.errors = error.response.data.errors || {};
          this.$toast.error("Please check the form for errors");
        } else {
          this.$toast.error("An error occurred while creating payment receipt");
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    resetForm() {
      this.form = {
        user_id: null,
        amount_received: "",
        mode_of_payment: null,
        date_of_payment: new Date().toISOString().substr(0, 10),
      };
      this.errors = {};
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>